import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { X } from "lucide-react";
import { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useGetChoices } from "../../../hooks";

export interface ScheduleFilters {
  status: string;
  frequency_type: string;
  page: number;
}

interface SchedulesFilterPanelProps {
  onFiltersChange: (filters: ScheduleFilters) => void;
  onApplyFilters: (filters: ScheduleFilters) => void;
  onClearFilters: () => void;
  loading?: boolean;
}

export const SchedulesFilterPanel = ({
  onFiltersChange,
  onApplyFilters,
  onClearFilters,
  loading = false,
}: SchedulesFilterPanelProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const statusData = [
    { key: 0, value: "Inactive" },
    { key: 1, value: "Active" },
  ];

  const { data: frequencyTypeData } = useGetChoices("frequency_type");

  const frequencyTypeOptions = useMemo(
    () =>
      frequencyTypeData?.map((choice) => ({
        value: choice.key,
        label: choice.value,
      })),
    [frequencyTypeData]
  );

  const statusOptions = useMemo(
    () =>
      statusData?.map((choice) => ({
        value: choice.key,
        label: choice.value,
      })),
    [statusData]
  );

  // Parse current URL parameters
  const getCurrentFiltersFromUrl = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return {
      status: searchParams.get("status") || "all",
      frequency_type: searchParams.get("frequency_type") || "all",
      page: 1,
    };
  }, [location.search]);

  const createFormFields = (): FormField[] => [
    {
      name: "status",
      label: "Schedule Status",
      type: "select",
      required: false,
      placeholder: "All Status",
      defaultValue: getCurrentFiltersFromUrl.status,
      options: statusOptions,
      classes: FIELD_CLASSES,
    },
    {
      name: "frequency_type",
      label: "Frequency Type",
      type: "select",
      required: false,
      placeholder: "All Frequencies",
      defaultValue: getCurrentFiltersFromUrl.frequency_type,
      options: frequencyTypeOptions,
      classes: FIELD_CLASSES,
    },
  ];

  const form = FormService.createForm({
    fields: createFormFields(),
  });

  const updateUrlWithFilters = (newFilters: ScheduleFilters) => {
    const searchParams = new URLSearchParams(location.search);

    // Clear existing filter parameters
    const filterParams = ["status", "frequency_type"];
    filterParams.forEach((param) => searchParams.delete(param));

    // Add new filter parameters (only non-'all' values)
    Object.entries(newFilters).forEach(([key, value]) => {
      if (
        value !== undefined &&
        value !== null &&
        value !== "" &&
        value !== "all"
      ) {
        searchParams.set(key, String(value));
      }
    });

    // Always reset to page 1 when filters change
    searchParams.set("page", "1");

    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });
  };

  const handleSubmit = (data: any) => {
    const processedData = { ...data, page: 1 };

    updateUrlWithFilters(processedData);
    onApplyFilters(processedData);
    setTimeout(() => {
      FormService.clearErrors(form);
    }, 500);
  };

  const handleReset = () => {
    const defaultFilters: ScheduleFilters = {
      status: "all",
      frequency_type: "all",
      page: 1,
    };

    form.reset(defaultFilters);

    const searchParams = new URLSearchParams();
    searchParams.set("page", "1");
    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });
    setTimeout(() => {
      FormService.clearErrors(form);
    }, 500);

    onClearFilters();
  };

  useEffect(() => {
    const urlFilters = getCurrentFiltersFromUrl;
    Object.keys(urlFilters).forEach((key) => {
      const value = urlFilters[key as keyof typeof urlFilters];
      if (value !== undefined) {
        form.setValue(key as keyof ScheduleFilters, value);
      }
    });
  }, [getCurrentFiltersFromUrl, form]);

  const renderCustomFooter = () => (
    <div className="flex justify-end gap-3 items-center pt-6 border-t border-gray-200">
      <Button variant="outline" onClick={handleReset} disabled={loading}>
        <X className="h-4 w-4" />
        Clear Filters
      </Button>

      <Button
        isLoading={loading}
        onClick={() => handleSubmit(form.getValues())}
      >
        Apply Filters
      </Button>
    </div>
  );

  return (
    <div className="space-y-4">
      <DynamicForm
        fields={createFormFields()}
        form={form}
        // onReset={handleReset}
        loading={loading}
        config={{
          showSubmitButton: false,
          showResetButton: false,
          layout: "vertical",
          spacing: "medium",
        }}
        gridColumns={2}
        responsiveColumns={{ sm: 1, md: 2 }}
        className="space-y-6"
        renderCustomFooter={renderCustomFooter}
      />
    </div>
  );
};
